import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { z } from 'zod';
import { Alert, CheckedInput, FormLabel } from '@src/components';
import FileInput from '@src/components/FileInput';
import { NumberStep } from '@src/components/NumberStep/NumberStep';
import PresignedDownloadButton from '@src/components/PresignedDownloadButton';
import {
  NifOrderUploadPoaDataFragment,
  useUploadNifOrderPoaMutation,
} from '@src/generated/graphql';
import { useStatus } from '@src/hooks/useStatus';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import { logError } from '@src/services/error-logger';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import { fileSchema } from '../../utils/file';
import { verificationCheckboxSchema } from '../utils/validation';
import signPoaImage from './sign-poa.png';

const formSchema = z.object({
  poaFile: fileSchema,
  verifications: z.object({
    fourCorners: verificationCheckboxSchema,
    signatureMatches: verificationCheckboxSchema,
    imageClear: verificationCheckboxSchema,
    noShadows: verificationCheckboxSchema,
  }),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  nifOrder: NifOrderUploadPoaDataFragment;
}

function NifOrderUploadPoaForm({ nifOrder }: Props): JSX.Element {
  const { handleSubmit, control } = useForm<FormData>({
    defaultValues: {},
    resolver: zodResolver(formSchema),
  });
  const [uploadNifOrderPoa] = useUploadNifOrderPoaMutation();
  const { status, setError } = useStatus();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation(['nif']);

  function onSubmit(formData: FormData): void {
    setIsSubmitting(true);
    uploadNifOrderPoa({
      variables: {
        input: {
          nifOrderId: nifOrder.id,
          poaFile: formData.poaFile,
        },
      },
    })
      .then(() => {
        toast.success(
          t(
            'upload_power_of_attorney_form.toast_success',
            'Power of attorney successfully uploaded!',
          ),
        );
        void navigate('/');
      })
      .catch((err) => {
        setError(logAndFormatError(err));
        setIsSubmitting(false);
      });
  }

  useEffect(() => {
    if (!nifOrder.poaTemplateFile) {
      logError(new Error('No Power of Attorney template file found!'));
      toast.error(
        t(
          'upload_power_of_attorney_form.toast_error',
          'No Power of Attorney template file found! Please contact Bordr support.',
        ),
      );
    }
  }, [nifOrder.poaTemplateFile, toast, t]);

  if (!nifOrder.poaTemplateFile) {
    return <Navigate to="/" />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
      <div className="space-y-2">
        <h2>
          {t('upload_power_of_attorney_form.title', 'Sign Power of Attorney')}
        </h2>
        <p>
          {t(
            'upload_power_of_attorney_form.subtitle',
            'The Power of Attorney allows your fiscal representative to request a NIF on your behalf.',
          )}
        </p>
      </div>
      <div className="space-y-3">
        <NumberStep number={1}>
          {t(
            'upload_power_of_attorney_form.download_step.title',
            'Download the document',
          )}
        </NumberStep>
        <div className="flex items-center justify-between rounded-md bg-gray-50 p-4">
          <p>Power of Attorney.pdf</p>
          <PresignedDownloadButton
            fileId={nifOrder.poaTemplateFile.id}
            color="white"
          >
            {t('buttons.download', 'Download', { ns: 'translation' })}
          </PresignedDownloadButton>
        </div>
      </div>
      <div className="space-y-3">
        <NumberStep number={2}>
          {t(
            'upload_power_of_attorney_form.print_and_sign_step.title',
            'Print and sign the document',
          )}
        </NumberStep>
        <p>
          {t(
            'upload_power_of_attorney_form.print_and_sign_step.body',
            'Make sure that your signature matches the one on your passport exactly.',
          )}
        </p>
      </div>
      <div className="space-y-3">
        <NumberStep number={3}>
          {t(
            'upload_power_of_attorney_form.upload_step.title',
            'Upload the signed document',
          )}
        </NumberStep>
        <Alert.WithStatus status={status} />

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div className="space-y-4">
            <FileInput.LabelledController
              category="reviewableDocumentVersionFileOne"
              label={t(
                'upload_power_of_attorney_form.upload_file_label',
                'Signed Power of Attorney',
              )}
              control={control}
              name="poaFile"
            />
            <FormLabel>
              {t(
                'upload_checklists.instructions',
                'Please verify the following regarding your upload:',
              )}
            </FormLabel>
            <CheckedInput.LabelledController
              control={control}
              name="verifications.fourCorners"
              label={t(
                'upload_power_of_attorney_form.checklist.four_corners',
                'Shows all 4 corners of the document',
              )}
            />
            <CheckedInput.LabelledController
              control={control}
              name="verifications.signatureMatches"
              label={t(
                'upload_power_of_attorney_form.checklist.passport_signature',
                'Signature matches passport',
              )}
            />
            <CheckedInput.LabelledController
              control={control}
              name="verifications.imageClear"
              label={t(
                'upload_power_of_attorney_form.checklist.clear_image',
                'Image is clear',
              )}
            />
            <CheckedInput.LabelledController
              control={control}
              name="verifications.noShadows"
              label={t(
                'upload_power_of_attorney_form.checklist.no_shadows_reflections',
                'No shadows or light reflections',
              )}
            />
          </div>
          <img
            src={signPoaImage}
            alt="Instructions for signing power of attorney"
          />
        </div>
      </div>

      <OrderNavigationButtons
        previousUrl={null}
        nextText={t('buttons.done', 'Done', { ns: 'translation' })}
        buttonType="submit"
        disabled={isSubmitting}
      />
    </form>
  );
}

export default NifOrderUploadPoaForm;
